import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar,
  Autocomplete, TextField, Avatar,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Close as CloseIcon,
  PersonAdd as CheckInIcon, Notifications as CallIcon,
  PlayArrow as StartIcon, CheckCircle as CompleteIcon,
  Timer as TimerIcon, Person as PersonIcon, ExitToApp as LeaveIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

const statusLabels: Record<string, string> = {
  waiting: 'Aguardando', checked_in: 'Check-in', called: 'Chamado',
  in_service: 'Em Atendimento', completed: 'Finalizado', left: 'Saiu',
};
const statusColors: Record<string, string> = {
  waiting: '#f59e0b', checked_in: '#3b82f6', called: '#8b5cf6',
  in_service: '#14B8A6', completed: '#22c55e', left: '#94a3b8',
};

const WaitingRoom: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();
  const [queue, setQueue] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const [form, setForm] = useState<any>({ patient_id: '', professional_id: '', appointment_type: 'consultation', priority: 5, notes: '' });

  useEffect(() => { loadData(); const interval = setInterval(loadData, 30000); return () => clearInterval(interval); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [queueRes, statsRes] = await Promise.all([
        axios.get(`${getApiUrl()}/api/clinic/waiting-room`),
        axios.get(`${getApiUrl()}/api/clinic/waiting-room/stats`),
      ]);
      setQueue(queueRes.data.data || []);
      setStats(statsRes.data.data || {});
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const searchPatients = async (q: string) => {
    try {
      const { data } = await axios.get(`${getApiUrl()}/api/clinic/prescriptions-search/patients`, { params: { search: q } });
      setPatientOptions(data.data || []);
    } catch (e) { console.error(e); }
  };

  const searchProfessionals = async (q: string) => {
    try {
      const { data } = await axios.get(`${getApiUrl()}/api/clinic/prescriptions-search/professionals`, { params: { search: q } });
      setProfOptions(data.data || []);
    } catch (e) { console.error(e); }
  };

  const handleCheckIn = async () => {
    try {
      await axios.post(`${getApiUrl()}/api/clinic/waiting-room/check-in`, form);
      setSnackbar({ open: true, message: 'Paciente registrado na sala de espera!', severity: 'success' });
      setOpenDialog(false); setForm({ patient_id: '', professional_id: '', appointment_type: 'consultation', priority: 5, notes: '' }); loadData();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await axios.patch(`${getApiUrl()}/api/clinic/waiting-room/${id}/status`, { status });
      setSnackbar({ open: true, message: `Status atualizado!`, severity: 'success' }); loadData();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const removeFromQueue = async (id: string) => {
    try {
      await axios.delete(`${getApiUrl()}/api/clinic/waiting-room/${id}`);
      setSnackbar({ open: true, message: 'Removido da fila!', severity: 'success' }); loadData();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: { xs: 2, md: 3 },
        gap: 2,
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>Sala de Espera</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Gerencie a fila de pacientes em tempo real</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="outlined" fullWidth={isMobile} startIcon={<RefreshIcon />} onClick={loadData} sx={{ textTransform: 'none', borderColor: '#14B8A6', color: '#14B8A6' }}>
            Atualizar
          </Button>
          <Button variant="contained" fullWidth={isMobile} startIcon={<CheckInIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }} onClick={() => setOpenDialog(true)}>
            Check-in
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Aguardando', value: stats.waiting || 0, color: '#f59e0b', icon: <TimerIcon /> },
          { label: 'Em Atendimento', value: stats.in_service || 0, color: '#14B8A6', icon: <PersonIcon /> },
          { label: 'Atendidos Hoje', value: stats.completed_today || 0, color: '#22c55e', icon: <CompleteIcon /> },
          { label: 'Tempo Médio', value: `${stats.avg_wait_minutes || 0} min`, color: '#3b82f6', icon: <TimerIcon /> },
        ].map((kpi, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ bgcolor: 'white', borderRadius: 3, border: `1px solid ${kpi.color}22` }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>{kpi.label}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
                </Box>
                <Box sx={{ color: kpi.color, opacity: 0.3, fontSize: 40 }}>{kpi.icon}</Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {queue.length === 0 ? (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center' }}>
              <TimerIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
              <Typography sx={{ color: '#94a3b8' }}>Nenhum paciente na sala de espera</Typography>
            </Card>
          </Grid>
        ) : queue.map((entry) => (
          <Grid item xs={12} sm={6} md={4} key={entry.id}>
            <Card sx={{
              borderRadius: 3, border: `2px solid ${statusColors[entry.status] || '#e2e8f0'}`,
              bgcolor: entry.status === 'called' ? '#f5f3ff' : entry.status === 'in_service' ? '#f0fdfa' : 'white',
              transition: 'all 0.3s',
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: statusColors[entry.status] || '#94a3b8', width: 36, height: 36, fontSize: 14, fontWeight: 700 }}>
                      {entry.queue_number || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{entry.patient_name || 'Paciente'}</Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>{entry.professional_name || '-'}</Typography>
                    </Box>
                  </Box>
                  <Chip label={statusLabels[entry.status] || entry.status} size="small"
                    sx={{ bgcolor: (statusColors[entry.status] || '#666') + '20', color: statusColors[entry.status] || '#666', fontWeight: 600, fontSize: '0.7rem' }} />
                </Box>

                {entry.current_wait_minutes !== undefined && entry.status !== 'in_service' && entry.status !== 'completed' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <TimerIcon sx={{ fontSize: 16, color: entry.current_wait_minutes > 30 ? '#ef4444' : '#f59e0b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: entry.current_wait_minutes > 30 ? '#ef4444' : '#f59e0b' }}>
                      {entry.current_wait_minutes} min esperando
                    </Typography>
                  </Box>
                )}

                {entry.notes && <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>{entry.notes}</Typography>}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, alignItems: 'center' }}>
                  {entry.status === 'waiting' && (
                    <Button size="small" variant="outlined" startIcon={<CallIcon />} onClick={() => updateStatus(entry.id, 'called')}
                      sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', fontSize: '0.75rem', textTransform: 'none' }}>Chamar</Button>
                  )}
                  {(entry.status === 'called' || entry.status === 'waiting') && (
                    <Button size="small" variant="outlined" startIcon={<StartIcon />} onClick={() => updateStatus(entry.id, 'in_service')}
                      sx={{ borderColor: '#14B8A6', color: '#14B8A6', fontSize: '0.75rem', textTransform: 'none' }}>Atender</Button>
                  )}
                  {entry.status === 'in_service' && (
                    <Button size="small" variant="contained" startIcon={<CompleteIcon />} onClick={() => updateStatus(entry.id, 'completed')}
                      sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, fontSize: '0.75rem', textTransform: 'none' }}>Finalizar</Button>
                  )}
                  <IconButton size="small" onClick={() => removeFromQueue(entry.id)} sx={{ color: '#ef4444', ml: { xs: 0, sm: 'auto' } }} aria-label="Remover da fila"><CloseIcon fontSize="small" /></IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, borderRadius: 0, height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column' }
            : {},
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, px: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Check-in de Paciente</Typography>
          <IconButton onClick={() => setOpenDialog(false)} aria-label="Fechar"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete size="small" options={patientOptions} getOptionLabel={(o: any) => o.full_name || ''}
                onInputChange={(_, v) => searchPatients(v)} onChange={(_, v) => setForm({ ...form, patient_id: v?.id || '' })}
                onOpen={() => searchPatients('')} renderInput={(params) => <TextField {...params} label="Paciente" />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete size="small" options={profOptions} getOptionLabel={(o: any) => o.full_name || ''}
                onInputChange={(_, v) => searchProfessionals(v)} onChange={(_, v) => setForm({ ...form, professional_id: v?.id || '' })}
                onOpen={() => searchProfessionals('')} renderInput={(params) => <TextField {...params} label="Profissional" />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Observações" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 }, flexShrink: 0, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button fullWidth={isMobile} onClick={() => setOpenDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button fullWidth={isMobile} variant="contained" onClick={handleCheckIn} sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}>Registrar Check-in</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WaitingRoom;
